import store from '@/store'

/**
 * Get the user from the auth module
 * @returns {Object}
 */
const getUser = () => {
  return store.state.auth?.user || {}
}

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission name to check
 * @returns {boolean}
 */
export const hasPermission = (permission) => {
  const user = getUser()

  // If user is Super-Admin or Admin, they have all permissions
  if (user.is_super_admin) {
    return true
  }

  // Check if user has the specific permission
  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions.includes(permission)
  }

  return false
}

/**
 * Check if user has any of the specified permissions
 * @param {string[]} permissions - Array of permission names
 * @returns {boolean}
 */
export const hasAnyPermission = (permissions) => {
  const user = getUser()

  if (user.is_super_admin) {
    return true
  }

  if (user.permissions && Array.isArray(user.permissions)) {
    return permissions.some(perm => user.permissions.includes(perm))
  }

  return false
}

/**
 * Check if user has all of the specified permissions
 * @param {string[]} permissions - Array of permission names
 * @returns {boolean}
 */
export const hasAllPermissions = (permissions) => {
  const user = getUser()

  if (user.is_super_admin) {
    return true
  }

  if (user.permissions && Array.isArray(user.permissions)) {
    return permissions.every(perm => user.permissions.includes(perm))
  }

  return false
}

/**
 * Check if user has a specific role
 * @param {string} role - Role name to check
 * @returns {boolean}
 */
export const hasRole = (role) => {
  const user = getUser()

  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(role)
  }

  return false
}

/**
 * Check if user is Super Admin or Admin
 * @returns {boolean}
 */
export const isSuperAdmin = () => {
  const user = getUser()
  return user.is_super_admin === true
}

/**
 * Permission mapping for menu items to permissions
 * This maps menu item IDs or links to required permissions
 */
export const menuPermissionMap = {
  // Administration
  '/users': ['admin.users.list'],
  '/roles': ['admin.roles.list'],

  // Event configuration
  '/event/setting': ['event.config'],
  '/event/emails': ['event.config'],
  '/event/wa': ['event.config'],
  '/event/authentication': ['event.config'],
  '/event/integration': ['event.config'],
  '/event/certificate': ['event.config'],
  '/event/customization': ['event.config'],
  '/event/timeline': ['event.config'],
  '/event/indication': ['event.config'],
  '/event/terms-conditions': ['event.config'],
  '/form-builder': ['formbuilder.list'],

  // Participants
  '/participants': ['participant.list'],

  // Rooms
  '/rooms': ['room.list'],

  // Speakers
  '/speakers': ['speaker.list'],

  // Schedules
  '/schedules': ['schedule.list'],

  // Archives
  '/archives': ['archive.list'],

  // Videos
  '/videos': ['video.list'],

  // Partners
  '/partners': ['partner.list'],

  // Survey
  '/survey': ['survey.add', 'survey.control'],

  // Gamification
  '/gamification': ['gamification.setting', 'gamification.control'],

  // Raffle
  '/raffle': ['raffle.new'],

  // Reports
  '/reports': [
    'reports.participant',
    'reports.indication',
    'reports.timeline',
    'reports.quiz',
    'reports.poll',
    'reports.asking',
    'reports.wordcloud',
    'reports.certificate',
    'reports.gamification',
    'reports.logsauth'
  ],

  // Campaigns
  '/email/campaign': ['menu.campaigns'],
  '/wa/campaign': ['menu.campaigns']
}

/**
 * Check if user can access a menu item based on its link
 * @param {string} link - Menu item link
 * @returns {boolean}
 */
export const canAccessMenu = (link) => {
  const user = getUser()

  // Super admin can access everything
  if (user.is_super_admin) {
    return true
  }

  // If no permission mapping exists for this link, allow access
  const requiredPermissions = menuPermissionMap[link]
  if (!requiredPermissions) {
    return true
  }

  // Check if user has any of the required permissions
  return hasAnyPermission(requiredPermissions)
}

export default {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  isSuperAdmin,
  canAccessMenu,
  menuPermissionMap
}
